import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/services/api";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Trophy,
  TrendingUp,
  Eye,
  ShoppingBag,
  DollarSign,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Ban,
  RefreshCw,
  Award,
  Instagram,
  Phone,
  Mail,
  Calendar,
  BarChart3,
  Image,
  Video,
  FileText,
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Sparkles,
  ChevronRight,
  MousePointer,
  Target,
  Wallet,
  Banknote,
  CreditCard,
  AlertCircle,
} from "lucide-react";

const NIVEL_CONFIG = {
  embajador: { name: "Embajador", color: "bg-gray-100 text-gray-700" },
  oro: { name: "Oro", color: "bg-amber-100 text-amber-700" },
  premium: { name: "Premium", color: "bg-purple-100 text-purple-700" },
  elite: { name: "Elite", color: "bg-rose-100 text-rose-700" },
};

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700", icon: Clock },
  aprobado: { label: "Aprobado", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rechazado: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
  suspendido: { label: "Suspendido", color: "bg-gray-100 text-gray-700", icon: Ban },
};

const CONTENT_TYPES = [
  { value: "foto", label: "Foto", icon: Image },
  { value: "video", label: "Video", icon: Video },
  { value: "historia", label: "Historia", icon: Image },
  { value: "flyer", label: "Flyer", icon: Image },
  { value: "reel", label: "Reel", icon: Video },
  { value: "texto", label: "Texto", icon: FileText },
  { value: "promocion", label: "Promoción", icon: Sparkles },
];

export default function AdminEmbajadores() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data
  const [stats, setStats] = useState(null);
  const [embajadores, setEmbajadores] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [contenido, setContenido] = useState([]);
  const [retirosPendientes, setRetirosPendientes] = useState([]);
  const [comisionConfig, setComisionConfig] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [rankingPeriodo, setRankingPeriodo] = useState("mes");
  
  // Dialogs
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [selectedEmbajador, setSelectedEmbajador] = useState(null);
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({
    tipo: "foto",
    titulo: "",
    descripcion: "",
    url: "",
    texto_sugerido: "",
    orden: 99,
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, embajadoresRes, pendientesRes, rankingRes, contenidoRes, retirosRes, configRes] = await Promise.all([
        api.get("/embajadores/admin/stats"),
        api.get("/embajadores/admin/lista"),
        api.get("/embajadores/admin/pendientes"),
        api.get(`/embajadores/admin/ranking?periodo=${rankingPeriodo}`),
        api.get("/embajadores/admin/contenido").catch(() => ({ data: [] })),
        api.get("/embajadores/comisiones/admin/retiros-pendientes").catch(() => ({ data: [] })),
        api.get("/embajadores/comisiones/admin/config").catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setEmbajadores(embajadoresRes.data);
      setPendientes(pendientesRes.data);
      setRanking(rankingRes.data);
      setContenido(contenidoRes.data || []);
      setRetirosPendientes(retirosRes.data || []);
      setComisionConfig(configRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [rankingPeriodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleAprobar = async (embajadorId) => {
    try {
      await api.put(`/embajadores/admin/${embajadorId}/aprobar`);
      toast.success("Embajador aprobado");
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar");
    }
  };

  const handleRechazar = async () => {
    if (!selectedEmbajador) return;
    try {
      await api.put(`/embajadores/admin/${selectedEmbajador.id}/rechazar`, {
        motivo: rejectMotivo
      });
      toast.success("Solicitud rechazada");
      setShowRejectDialog(false);
      setSelectedEmbajador(null);
      setRejectMotivo("");
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar");
    }
  };

  const handleSuspender = async (embajadorId) => {
    if (!confirm("¿Suspender este embajador?")) return;
    try {
      await api.put(`/embajadores/admin/${embajadorId}/suspender`);
      toast.success("Embajador suspendido");
      fetchData();
    } catch (error) {
      toast.error("Error al suspender");
    }
  };

  const handleSaveContent = async () => {
    try {
      if (editingContent) {
        await api.put(`/embajadores/admin/contenido/${editingContent.id}`, contentForm);
        toast.success("Contenido actualizado");
      } else {
        await api.post("/embajadores/admin/contenido", contentForm);
        toast.success("Contenido creado");
      }
      setShowContentDialog(false);
      setEditingContent(null);
      setContentForm({ tipo: "foto", titulo: "", descripcion: "", url: "", texto_sugerido: "", orden: 99 });
      fetchData();
    } catch (error) {
      toast.error("Error al guardar contenido");
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!confirm("¿Eliminar este contenido?")) return;
    try {
      await api.delete(`/embajadores/admin/contenido/${contentId}`);
      toast.success("Contenido eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handlePagarRetiro = async (retiroId) => {
    const referencia = prompt("Ingresa la referencia de pago (opcional):");
    try {
      await api.put(`/embajadores/comisiones/admin/retiros/${retiroId}/pagar`, {
        referencia_pago: referencia || ""
      });
      toast.success("Retiro marcado como pagado");
      fetchData();
    } catch (error) {
      toast.error("Error al procesar pago");
    }
  };

  const handleRechazarRetiro = async (retiroId) => {
    const motivo = prompt("Motivo del rechazo:");
    if (!motivo) return;
    try {
      await api.put(`/embajadores/comisiones/admin/retiros/${retiroId}/rechazar`, {
        motivo
      });
      toast.success("Retiro rechazado");
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const filteredEmbajadores = embajadores.filter(e => {
    const matchSearch = e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       e.codigo_afiliado?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === "todos" || e.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C8902A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A2C0A]/60">Cargando embajadores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-embajadores">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-['Playfair_Display'] font-bold text-[#4A2C0A] flex items-center gap-2">
            <Award className="w-7 h-7 text-amber-500" />
            Programa de Embajadores
          </h1>
          <p className="text-[#4A2C0A]/60 mt-1">
            Gestiona tu red de afiliados y marketing
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A2C0A]">{stats?.total_embajadores || 0}</p>
              <p className="text-xs text-[#4A2C0A]/60">Embajadores</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A2C0A]">{stats?.solicitudes_pendientes || 0}</p>
              <p className="text-xs text-[#4A2C0A]/60">Pendientes</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <MousePointer className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A2C0A]">{stats?.clicks_mes || 0}</p>
              <p className="text-xs text-[#4A2C0A]/60">Clicks / mes</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A2C0A]">{stats?.pedidos_mes || 0}</p>
              <p className="text-xs text-[#4A2C0A]/60">Pedidos / mes</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A2C0A]">{stats?.tasa_conversion || 0}%</p>
              <p className="text-xs text-[#4A2C0A]/60">Conversión</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#FBF5E8] border border-[#E5D4B3]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="pendientes" className="data-[state=active]:bg-white">
            <Clock className="w-4 h-4 mr-2" />
            Pendientes
            {pendientes.length > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pendientes.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="embajadores" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Todos
          </TabsTrigger>
          <TabsTrigger value="ranking" className="data-[state=active]:bg-white">
            <Trophy className="w-4 h-4 mr-2" />
            Ranking
          </TabsTrigger>
          <TabsTrigger value="contenido" className="data-[state=active]:bg-white">
            <Image className="w-4 h-4 mr-2" />
            Contenido
          </TabsTrigger>
          <TabsTrigger value="retiros" className="data-[state=active]:bg-white">
            <Wallet className="w-4 h-4 mr-2" />
            Retiros
            {retirosPendientes.length > 0 && (
              <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                {retirosPendientes.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Solicitudes Pendientes Quick View */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  Solicitudes Pendientes
                </h2>
                {pendientes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab("pendientes")}
                    className="text-[#C8902A]"
                  >
                    Ver todas
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
              
              {pendientes.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p className="text-[#4A2C0A]/60">No hay solicitudes pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendientes.slice(0, 3).map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                          <span className="font-semibold text-orange-700">{e.nombre?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-[#4A2C0A]">{e.nombre}</p>
                          <p className="text-xs text-[#4A2C0A]/60">{e.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleAprobar(e.id)}
                          className="bg-green-500 hover:bg-green-600 h-8 w-8 p-0"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedEmbajador(e); setShowRejectDialog(true); }}
                          className="border-red-300 text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Embajadores */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Top Embajadores
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab("ranking")}
                  className="text-[#C8902A]"
                >
                  Ver ranking
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              {ranking.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-[#4A2C0A]/60">Sin datos de ranking</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ranking.slice(0, 5).map((r, idx) => (
                    <div key={r.embajador_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500" :
                        idx === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                        idx === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700" :
                        "bg-gray-200 text-gray-600"
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#4A2C0A] truncate">{r.nombre}</p>
                        <p className="text-xs text-[#4A2C0A]/60">{r.total_pedidos} pedidos</p>
                      </div>
                      <p className="font-semibold text-[#C8902A]">{formatCurrency(r.total_ventas)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pendientes Tab */}
        <TabsContent value="pendientes" className="mt-6">
          <div className="bg-white rounded-xl shadow-sm border border-[#E5D4B3]">
            {pendientes.length === 0 ? (
              <div className="text-center py-16">
                <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-300" />
                <h3 className="text-lg font-semibold text-[#4A2C0A] mb-2">¡Todo al día!</h3>
                <p className="text-[#4A2C0A]/60">No hay solicitudes pendientes de revisión</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E5D4B3]">
                {pendientes.map((e) => (
                  <div key={e.id} className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                          <span className="text-xl font-bold text-amber-600">{e.nombre?.charAt(0)}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#4A2C0A] text-lg">{e.nombre}</h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-[#4A2C0A]/70">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {e.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {e.telefono}
                            </span>
                            {e.instagram && (
                              <span className="flex items-center gap-1">
                                <Instagram className="w-3 h-3" />
                                {e.instagram}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-[#4A2C0A]/50">
                            <Calendar className="w-3 h-3" />
                            Registrado: {formatDate(e.fecha_registro)}
                            {e.como_conociste && (
                              <span className="ml-2 bg-[#FBF5E8] px-2 py-0.5 rounded">
                                Origen: {e.como_conociste}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 lg:flex-shrink-0">
                        <Button
                          onClick={() => handleAprobar(e.id)}
                          className="gap-2 bg-green-500 hover:bg-green-600"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Aprobar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setSelectedEmbajador(e); setShowRejectDialog(true); }}
                          className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Todos los Embajadores Tab */}
        <TabsContent value="embajadores" className="mt-6">
          <div className="bg-white rounded-xl shadow-sm border border-[#E5D4B3]">
            {/* Filters */}
            <div className="p-4 border-b border-[#E5D4B3] flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre, email o código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 border-[#E5D4B3]"
                />
              </div>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger className="w-[150px] border-[#E5D4B3]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aprobado">Aprobados</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="rechazado">Rechazados</SelectItem>
                  <SelectItem value="suspendido">Suspendidos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF5E8] text-xs uppercase text-[#4A2C0A]/60">
                  <tr>
                    <th className="px-4 py-3 text-left">Embajador</th>
                    <th className="px-4 py-3 text-left">Código</th>
                    <th className="px-4 py-3 text-center">Nivel</th>
                    <th className="px-4 py-3 text-center">Estado</th>
                    <th className="px-4 py-3 text-right">Pedidos</th>
                    <th className="px-4 py-3 text-right">Ventas</th>
                    <th className="px-4 py-3 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5D4B3]">
                  {filteredEmbajadores.map((e) => {
                    const estadoConfig = ESTADO_CONFIG[e.estado] || ESTADO_CONFIG.pendiente;
                    const nivelConfig = NIVEL_CONFIG[e.nivel] || NIVEL_CONFIG.embajador;
                    
                    return (
                      <tr key={e.id} className="hover:bg-[#FBF5E8]/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center">
                              <span className="font-semibold text-amber-600 text-sm">{e.nombre?.charAt(0)}</span>
                            </div>
                            <div>
                              <p className="font-medium text-[#4A2C0A]">{e.nombre}</p>
                              <p className="text-xs text-[#4A2C0A]/60">{e.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-[#FBF5E8] px-2 py-1 rounded text-amber-700">
                            {e.codigo_afiliado}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${nivelConfig.color}`}>
                            {nivelConfig.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${estadoConfig.color}`}>
                            <estadoConfig.icon className="w-3 h-3" />
                            {estadoConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-[#4A2C0A]">
                          {e.stats?.total_orders || 0}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-[#C8902A]">
                          {formatCurrency(e.stats?.total_sales || 0)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1">
                            {e.estado === "pendiente" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleAprobar(e.id)}
                                  className="h-7 w-7 p-0 bg-green-500 hover:bg-green-600"
                                >
                                  <CheckCircle2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setSelectedEmbajador(e); setShowRejectDialog(true); }}
                                  className="h-7 w-7 p-0 border-red-300 text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                            {e.estado === "aprobado" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSuspender(e.id)}
                                className="h-7 w-7 p-0 border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                <Ban className="w-3 h-3" />
                              </Button>
                            )}
                            {e.estado === "suspendido" && (
                              <Button
                                size="sm"
                                onClick={() => handleAprobar(e.id)}
                                className="h-7 w-7 p-0 bg-green-500 hover:bg-green-600"
                              >
                                <CheckCircle2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredEmbajadores.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-[#4A2C0A]/60">No se encontraron embajadores</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Ranking Tab */}
        <TabsContent value="ranking" className="mt-6">
          <div className="bg-white rounded-xl shadow-sm border border-[#E5D4B3]">
            <div className="p-4 border-b border-[#E5D4B3] flex items-center justify-between">
              <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Ranking de Embajadores
              </h2>
              <Select value={rankingPeriodo} onValueChange={setRankingPeriodo}>
                <SelectTrigger className="w-[150px] border-[#E5D4B3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="semana">Esta semana</SelectItem>
                  <SelectItem value="mes">Este mes</SelectItem>
                  <SelectItem value="todo">Todo el tiempo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {ranking.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-lg font-semibold text-[#4A2C0A] mb-2">Sin datos</h3>
                <p className="text-[#4A2C0A]/60">No hay ventas en este período</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E5D4B3]">
                {ranking.map((r, idx) => (
                  <div key={r.embajador_id} className={`p-4 flex items-center gap-4 ${idx < 3 ? "bg-amber-50/50" : ""}`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl ${
                      idx === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg" :
                      idx === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400 text-white" :
                      idx === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700 text-white" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {idx + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#4A2C0A]">{r.nombre}</h3>
                        <code className="text-xs bg-[#FBF5E8] px-2 py-0.5 rounded text-amber-600">
                          {r.codigo}
                        </code>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-[#4A2C0A]/60">
                        <span className="flex items-center gap-1">
                          <ShoppingBag className="w-3 h-3" />
                          {r.total_pedidos} pedidos
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          {r.pedidos_entregados} entregados
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#C8902A]">{formatCurrency(r.total_ventas)}</p>
                      <p className="text-xs text-[#4A2C0A]/60">en ventas</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Contenido Tab */}
        <TabsContent value="contenido" className="mt-6">
          <div className="bg-white rounded-xl shadow-sm border border-[#E5D4B3]">
            <div className="p-4 border-b border-[#E5D4B3] flex items-center justify-between">
              <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
                <Image className="w-5 h-5 text-pink-500" />
                Biblioteca de Contenido
              </h2>
              <Button
                onClick={() => {
                  setEditingContent(null);
                  setContentForm({ tipo: "foto", titulo: "", descripcion: "", url: "", texto_sugerido: "", orden: 99 });
                  setShowContentDialog(true);
                }}
                className="gap-2 bg-[#C8902A] hover:bg-[#A67720]"
              >
                <Plus className="w-4 h-4" />
                Agregar Contenido
              </Button>
            </div>

            {contenido.length === 0 ? (
              <div className="text-center py-16">
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-lg font-semibold text-[#4A2C0A] mb-2">Sin contenido</h3>
                <p className="text-[#4A2C0A]/60 mb-4">Agrega material para que los embajadores compartan</p>
                <Button
                  onClick={() => setShowContentDialog(true)}
                  className="bg-[#C8902A] hover:bg-[#A67720]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear contenido
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {contenido.map((c) => {
                  const typeConfig = CONTENT_TYPES.find(t => t.value === c.tipo) || CONTENT_TYPES[0];
                  
                  return (
                    <div key={c.id} className="border border-[#E5D4B3] rounded-xl overflow-hidden">
                      {c.url && (c.tipo === "foto" || c.tipo === "flyer" || c.tipo === "historia") && (
                        <div className="aspect-video bg-gray-100">
                          <img src={c.url} alt={c.titulo} className="w-full h-full object-cover" />
                        </div>
                      )}
                      
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-700 flex items-center gap-1">
                            <typeConfig.icon className="w-3 h-3" />
                            {typeConfig.label}
                          </span>
                          {!c.activo && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              Inactivo
                            </span>
                          )}
                        </div>
                        
                        <h3 className="font-semibold text-[#4A2C0A] mb-1">{c.titulo}</h3>
                        {c.descripcion && (
                          <p className="text-sm text-[#4A2C0A]/60 line-clamp-2">{c.descripcion}</p>
                        )}
                        
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingContent(c);
                              setContentForm({
                                tipo: c.tipo,
                                titulo: c.titulo,
                                descripcion: c.descripcion || "",
                                url: c.url || "",
                                texto_sugerido: c.texto_sugerido || "",
                                orden: c.orden || 99,
                              });
                              setShowContentDialog(true);
                            }}
                            className="flex-1"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteContent(c.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Retiros Tab */}
        <TabsContent value="retiros" className="mt-6">
          <div className="space-y-6">
            {/* Config Card */}
            {comisionConfig && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5D4B3]">
                <h3 className="font-semibold text-[#4A2C0A] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Configuración de Comisiones
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-[#4A2C0A]/60">Base (Embajador)</p>
                    <p className="text-xl font-bold text-[#4A2C0A]">{comisionConfig.porcentaje_base}%</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs text-amber-700">Oro</p>
                    <p className="text-xl font-bold text-amber-700">{comisionConfig.porcentaje_oro}%</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-700">Premium</p>
                    <p className="text-xl font-bold text-purple-700">{comisionConfig.porcentaje_premium}%</p>
                  </div>
                  <div className="bg-rose-50 rounded-lg p-3">
                    <p className="text-xs text-rose-700">Elite</p>
                    <p className="text-xl font-bold text-rose-700">{comisionConfig.porcentaje_elite}%</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-[#4A2C0A]/60">Monto mínimo de retiro:</span>
                  <span className="font-semibold text-[#4A2C0A]">{formatCurrency(comisionConfig.monto_minimo_retiro)}</span>
                </div>
              </div>
            )}

            {/* Retiros Pendientes */}
            <div className="bg-white rounded-xl shadow-sm border border-[#E5D4B3]">
              <div className="p-4 border-b border-[#E5D4B3] flex items-center justify-between">
                <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  Solicitudes de Retiro Pendientes
                  {retirosPendientes.length > 0 && (
                    <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                      {retirosPendientes.length}
                    </span>
                  )}
                </h2>
              </div>

              {retirosPendientes.length === 0 ? (
                <div className="text-center py-16">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-300" />
                  <h3 className="text-lg font-semibold text-[#4A2C0A] mb-2">¡Todo al día!</h3>
                  <p className="text-[#4A2C0A]/60">No hay solicitudes de retiro pendientes</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E5D4B3]">
                  {retirosPendientes.map((retiro) => (
                    <div key={retiro.id} className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                            <Banknote className="w-7 h-7 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#4A2C0A] text-lg">
                              {formatCurrency(retiro.monto)}
                            </h3>
                            <p className="text-sm text-[#4A2C0A]/70 font-medium">{retiro.embajador_nombre}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#4A2C0A]/60">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Solicitado: {formatDate(retiro.fecha_solicitud)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col lg:items-end gap-3">
                          {/* Datos bancarios */}
                          <div className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex items-center gap-2 text-[#4A2C0A]">
                              <CreditCard className="w-4 h-4 text-gray-500" />
                              <span className="font-medium">{retiro.banco}</span>
                            </div>
                            <p className="text-[#4A2C0A]/70 mt-1">
                              {retiro.tipo_cuenta === "ahorros" ? "Ahorros" : "Corriente"} •••• {retiro.numero_cuenta?.slice(-4)}
                            </p>
                            <p className="text-[#4A2C0A]/60 text-xs">{retiro.titular_cuenta}</p>
                          </div>
                          
                          {/* Acciones */}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handlePagarRetiro(retiro.id)}
                              className="gap-2 bg-green-500 hover:bg-green-600"
                              data-testid={`pagar-retiro-${retiro.id}`}
                            >
                              <Banknote className="w-4 h-4" />
                              Marcar Pagado
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleRechazarRetiro(retiro.id)}
                              className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`rechazar-retiro-${retiro.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {retiro.notas && (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800">
                            <AlertCircle className="w-4 h-4 inline mr-1" />
                            Nota del embajador: {retiro.notas}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">Rechazar Solicitud</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#4A2C0A]/70 mb-4">
              ¿Estás seguro de rechazar la solicitud de <strong>{selectedEmbajador?.nombre}</strong>?
            </p>
            <Textarea
              placeholder="Motivo del rechazo (opcional)"
              value={rejectMotivo}
              onChange={(e) => setRejectMotivo(e.target.value)}
              className="border-[#E5D4B3]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRechazar} className="bg-red-500 hover:bg-red-600">
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Dialog */}
      <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">
              {editingContent ? "Editar Contenido" : "Nuevo Contenido"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">Tipo</label>
              <Select
                value={contentForm.tipo}
                onValueChange={(v) => setContentForm({ ...contentForm, tipo: v })}
              >
                <SelectTrigger className="border-[#E5D4B3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">Título</label>
              <Input
                value={contentForm.titulo}
                onChange={(e) => setContentForm({ ...contentForm, titulo: e.target.value })}
                className="border-[#E5D4B3]"
                placeholder="Título del contenido"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">Descripción</label>
              <Textarea
                value={contentForm.descripcion}
                onChange={(e) => setContentForm({ ...contentForm, descripcion: e.target.value })}
                className="border-[#E5D4B3]"
                placeholder="Descripción breve"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">URL del archivo</label>
              <Input
                value={contentForm.url}
                onChange={(e) => setContentForm({ ...contentForm, url: e.target.value })}
                className="border-[#E5D4B3]"
                placeholder="https://..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">Texto sugerido para compartir</label>
              <Textarea
                value={contentForm.texto_sugerido}
                onChange={(e) => setContentForm({ ...contentForm, texto_sugerido: e.target.value })}
                className="border-[#E5D4B3]"
                placeholder="Texto que los embajadores pueden copiar..."
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#4A2C0A] mb-1">Orden</label>
              <Input
                type="number"
                value={contentForm.orden}
                onChange={(e) => setContentForm({ ...contentForm, orden: parseInt(e.target.value) || 99 })}
                className="border-[#E5D4B3] w-24"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContentDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveContent} className="bg-[#C8902A] hover:bg-[#A67720]">
              {editingContent ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
